/** venture/VicsStage.tsx — VICS 화면 1개(1313·1319·1320)의 입력값 표 + 첨부 서류 + 완료 체크 (2026-09-24)
 *  값: 자동(앞단계 연동)이면 그대로 두고, 고쳐 쓰면 직접값(s13 txt)이 우선 · ↺ 로 자동값 복귀 · 값마다 복사
 *  서류: 위쪽 「서류」 띠(VicsDocs)에 업로드·발급 사이트(+ID·PW)·보기·🔍 인식을 모으고, 값 행에는 📄(미리보기 보며 입력 모달)와 복사만 둔다 */
import React, { useState } from 'react';
import { mutStore } from './stepcommon';
import { fmtComma, onlyDigits } from './bondcalc';
import VicsDocs, { docLoader } from './VicsDocs';
import DocFieldModal from './DocFieldModal';
import type { ModalField } from './DocFieldModal';
import { vicsVal, vicsSlot, vicsDoc, vicsDone, vicsStageText, vicsSrc, VICS_DOC_SITES } from './vics13';
import type { VicsStage as Stage, VicsField, VicsDocKey } from './vics13';
import type { Project, CoCard } from './types';

interface Props { p: Project; co: CoCard; stage: Stage; open: boolean; onToggle: () => void; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void; }

export async function copyText(text: string, toast: (m: string) => void, ok?: string): Promise<void> {
  try { await navigator.clipboard.writeText(text); toast(ok || '복사했습니다'); } catch { toast('복사 실패 — 직접 선택해 복사해 주세요'); }
}
const DOC_SHORT: Record<string, string> = { regcb: '등기부등본', biz: '사업자등록증', vcert: '벤처기업확인서', fundout: '입금내역' };

export default function VicsStage({ p, co, stage, open, onToggle, onPatch, toast }: Props) {
  const done = vicsDone(p, co.id, stage.key);
  const [modal, setModal] = useState<{ key: VicsDocKey; i: number } | null>(null);   // 미리보기 보며 입력: 서류 + 그 서류에서 읽는 항목 순번
  // 직접 입력 — 서류 인식 표식(src)은 사용자가 고쳐 쓰는 순간 지운다
  const setTxt = (slot: string, v: string) => onPatch(mutStore(p, 's13', (s) => {
    s.txt = s.txt || {}; if (v) s.txt[vicsSlot(co.id, slot)] = v; else delete s.txt[vicsSlot(co.id, slot)];
    if (s.src) delete s.src[vicsSlot(co.id, slot)];
  }));
  const toggleDone = () => onPatch(mutStore(p, 's13', (s) => { s.chk = s.chk || {}; const k = vicsSlot(co.id, 'done_' + stage.key); s.chk[k] = !s.chk[k]; }));
  const fieldsOf = (key: VicsDocKey): VicsField[] => stage.fields.filter((f) => f.doc === key);
  const openModal = (key: VicsDocKey, slot?: string) => {
    const list = fieldsOf(key); const i = Math.max(0, list.findIndex((f) => f.slot === slot));
    setModal({ key, i });
  };
  /** 값 옆 서류 버튼: 파일이 있을 때만 — 미리보기 보며 입력 모달 (없으면 위 「서류」 띠에서 올린다) */
  const docBtn = (f: VicsField) => {
    const key = f.doc;
    if (!key) return null;
    const d = vicsDoc(p, co, key);
    if (!d.name) return <button type="button" className="rbtn ghost" title={(DOC_SHORT[key] || '서류') + '이(가) 없습니다 — 누르면 위 「서류」 칸으로 갑니다'} onClick={() => { const el = document.getElementById('vdocs-' + co.id + '-' + stage.key); if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }}>{DOC_SHORT[key] || '서류'} 없음 ↑</button>;
    return <button type="button" className="rbtn" title={d.label + ' — 서류를 보면서 이 값을 입력합니다'} onClick={() => openModal(key, f.slot)}>📄 {DOC_SHORT[key] || '서류'} 보기</button>;
  };
  const srcPill = (v: { manual: string; auto: string }, f: VicsField) => {
    const src = v.manual ? vicsSrc(p, co.id, f.slot) : '';
    if (src.startsWith('doc:')) return <span className="pill p-green" title={'첨부 ' + (DOC_SHORT[src.slice(4)] || '서류') + '에서 읽은 값 — 고쳐 쓰면 직접 입력이 됩니다'}>📄 서류에서 읽음</span>;
    if (v.manual) return <span className="pill p-amber" title="직접 입력값 · ↺ 로 자동값 복귀">직접 입력</span>;
    if (v.auto) return <span className="pill p-blue" title={f.autoLabel}>{f.autoLabel || '자동'}</span>;
    return <span className="pill p-gray">미입력</span>;
  };

  return (
    <div className={'vstage' + (done ? ' done' : '') + (open ? ' open' : '')}>
      <div className="vstage-h" onClick={onToggle}>
        <span className="vno">{stage.key}</span><b>{stage.title}</b>
        <span className={'pill ' + (done ? 'p-green' : 'p-gray')} style={{ marginLeft: 6 }}>{done ? '✓ 완료' : stage.fields.length ? stage.fields.length + '개 항목' : '버튼만'}</span>
        <span className="grow" /><span className="s3muted">{open ? '접기 ▴' : '펼치기 ▾'}</span>
      </div>
      {open ? (
        <div className="vstage-b">
          <div className="snote">{stage.note}</div>
          {stage.docs.length ? <VicsDocs p={p} co={co} stage={stage} onPatch={onPatch} toast={toast} onView={(k) => openModal(k)} /> : null}
          {stage.fields.length ? (
            <div className="vtbl">
              {stage.fields.map((f) => {
                const v = vicsVal(p, co, f);
                const isNum = f.kind === 'num';
                const shown = isNum ? fmtComma(v.value) : v.value;
                const onChange = (raw: string) => { const nv = isNum ? onlyDigits(raw) : raw; setTxt(f.slot, nv === v.auto ? '' : nv); };
                return (
                  <div className="vrow" key={f.slot}>
                    <div className="vrow-l"><span className="vl">{f.label}</span>{srcPill(v, f)}</div>
                    <div className="vrow-c">
                      <input className="cotext" type={f.kind === 'date' ? 'date' : 'text'} inputMode={isNum ? 'numeric' : undefined} value={shown} placeholder={f.hint || (f.auto ? '' : '직접 입력')} onChange={(e) => onChange(e.target.value)} />
                      {v.manual && v.auto ? <button type="button" className="rbtn" title="자동값으로 되돌리기" onClick={() => setTxt(f.slot, '')}>↺</button> : null}
                      {docBtn(f)}
                      <button type="button" className="rbtn" disabled={!v.value} title="값 복사 (숫자는 콤마 없이)" onClick={() => copyText(v.value, toast)}>복사</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
          <div className="vfoot">
            {stage.fields.length ? <button type="button" className="rbtn" onClick={() => copyText(vicsStageText(p, co, stage), toast, stage.key + ' 입력값 전체를 복사했습니다')}>📋 전체 복사</button> : null}
            <span className="grow" />
            <button type="button" className={'chkpill sm ' + (done ? 'on' : '')} onClick={toggleDone}>{done ? '✓ VICS 입력 완료' : 'VICS 입력 완료로 표시'}</button>
          </div>
        </div>
      ) : null}
      {modal ? (() => {
        const d = vicsDoc(p, co, modal.key);
        const fields: ModalField[] = fieldsOf(modal.key).map((f) => {
          const v = vicsVal(p, co, f);
          return { key: f.slot, label: f.label, value: v.value, kind: f.kind, hint: f.hint, onChange: (nv) => setTxt(f.slot, nv === v.auto ? '' : nv) };
        });
        const site = VICS_DOC_SITES[modal.key];
        return <DocFieldModal title={(co.name || '법인') + ' · ' + d.label} fileName={d.name} load={docLoader(p, d) || undefined}
          noneMsg={'파일이 없습니다 — 값 옆 「⬆ 업로드」로 올리거나' + (site ? ' ' + site.label + '에서 발급받아 주세요' : ' 앞단계에서 올려 주세요')}
          fields={fields} index={modal.i} onIndex={(i) => setModal({ key: modal.key, i })} onClose={() => setModal(null)} />;
      })() : null}
    </div>
  );
}
